import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { MenuKeys } from "@models/interfaces/components/Menu.interface";
import { BottomSheetService } from "@services/components/bottom-sheet.service";
import { MenuContainerComponent } from "@components/minisite/topbar/menu/menu-container/menu-container.component";
import { LayoutService } from "@services/layout.service";
import { Subscription, take } from "rxjs";

@Injectable()
export class MenuContainerService {

    // SERVICES
    private readonly layout:        LayoutService         = inject(LayoutService);
    private readonly bottomSheet:   BottomSheetService    = inject(BottomSheetService);

    // STATE
    private readonly selectedKey:   WritableSignal<MenuKeys|null> = signal<MenuKeys|null>(null);
    public  readonly showContainer: WritableSignal<boolean|null>  = signal(null);
    public  readonly isAnimating:   WritableSignal<boolean>       = signal(false);
    public  readonly animation:     number                        = 400;
    private bottomSheetClose:       Subscription | null           = null;
    private menuTimer:              ReturnType<typeof setTimeout> | null = null;

    // GETTERS
    /**
     * Devuelve la clave del menu seleccionado actualmente.
     *
     * @returns Clave del menu abierto o `null` si no hay ninguno.
     */
    public get selectedMenuKey(): MenuKeys | null {

        return this.selectedKey();

    }

    // PUBLIC METHODS

    /**
     * Método principal para mostrar u ocultar el contenedor de menú además de cambiar el menú seleccionado.
     * Principalmente el método se encarga de cambiar el menú seleccionado y mostrar u ocultar el contenedor de menú. Para ello, 
     * comprueba si no se está ejecutando una animación para no superponerlas. 
     * 
     * 1. Si no hay un menú seleccionado, se selecciona el menú y se muestra el contenedor de menú.
     * 2. Si ya hay un menú seleccionado, se comprueba que no sea el mismo menú el que se quiere seleccionar. En caso de serlo, se oculta el contenedor de menú.
     *    Si es diferente, se cierra el contenedor y se espera lo que dura la animación para hacer el cambio de menú y reabrir el contenedor.
     * @param key Clave del menu que se debe abrir, cerrar o intercambiar.
     */
    public toggleMenuContainer(key: MenuKeys): void {

        // Si esta animando no hace nada
        if(this.isAnimating()) return;
        this.clearMenuTimer();

        // Si no hay un menú seleccionado, se selecciona el menú y se muestra el contenedor de menú.
        if (this.selectedKey() === null) {

            this.setMenuSelected(key);
            this.showContainer.set(true);
            
            if(this.layout.isMobile()) {
                this.bottomSheetClose?.unsubscribe();
                this.bottomSheetClose = this.bottomSheet.open(MenuContainerComponent).close
                    .pipe(take(1))
                    .subscribe((): void => {
                        const selectedKey: MenuKeys | null = this.selectedKey();
                        if(selectedKey) this.toggleMenuContainer(selectedKey);
                    });
            }

        }      
        // Si el menú seleccionado no es el mismo que ya esta seleccionado, se cierra el contenedor de menú, se espera a que termine la animación y se cambia el menú seleccionado.
        else if (this.selectedKey() !== key) {
            
            this.showContainer.set(false);
            
            this.menuTimer = window.setTimeout((): void => {

                this.setMenuSelected(key);           // Establece el menú seleccionado
                this.showContainer.set(true);   // Muestra el contenedor de menú
                this.menuTimer = null;

            }, this.animation);
            
        }
        // Si se selecciona el mismo menú que ya estaba seleccionado, actua de toggler ocultando el contenedor de menú y cambiando el menú y el menu item seleccionado a null.
        else {
    
            this.showContainer.set(false)
            // this.setMenuItem(null); // Cambia el menu item seleccionado a null    
            this.menuTimer = window.setTimeout((): void => {
                this.selectedKey.set(null);
                this.menuTimer = null;
            }, this.animation);

        }
    }

    /**
     * Método que cierra el contenedor de menú si hay un menú seleccionado.
     * 
     * Este método se utiliza para cerrar el contenedor de menú cuando se selecciona un elemento del menú o se hace hover sobre él.
     * Si no hay un menú seleccionado, no hace nada.
     */
    public closeMenuContainer(): void {

        if(this.selectedKey() === null) return;

        this.toggleMenuContainer(this.selectedKey()!);
    }

    /**
     * Establece la clave de menu seleccionada si es distinta a la actual.
     *
     * @param menuKey Clave del menu que debe quedar seleccionado.
     */
    public setMenuSelected(menuKey: MenuKeys): void {
        
        if(this.selectedKey() !== menuKey) this.selectedKey.set(menuKey);

    }

    /**
     * Cancela el temporizador pendiente de cambio o cierre de menu.
     */
    private clearMenuTimer(): void {
        if(!this.menuTimer) return;
        clearTimeout(this.menuTimer);
        this.menuTimer = null;
    }

}
